import React from 'react'
import { Navigate, redirect, RouteObject } from 'react-router-dom'
import loadable from '@loadable/component'

import { SiteUrls } from '@/routes/siteUrls'

const MainLayout = loadable(() => import('@/layouts/MainLayout'))
const NotSignedInLayout = loadable(() => import('@/layouts/NotSignedInLayout'))
const LandingPage = loadable(() => import('@/pages/landing/LandingPage'))

// Login
const LoginLayout = loadable(() => import('@/pages/Login/LoginLayout'))
const LoginMain = loadable(() => import('@/pages/Login/Login'))
const SignUp = loadable(() => import('@/pages/Login/SignUp'))
const RecoverPassword = loadable(() => import('@/pages/Login/RecoverPassword'))
const ExpiredUrl = loadable(() => import('@/pages/Login/ExpiredUrl'))

// Workspace
const NoWorkspace = loadable(() => import('@/pages/Workspace/NoWorkspace'))
const JoinWorkspace = loadable(() => import('@/pages/Workspace/Join'))
const CreateWorkspace = loadable(() => import('@/pages/Workspace/Create'))
const WorkspaceMain = loadable(() => import('@/pages/Workspace/Main'))
const WorkspaceSettings = loadable(() => import('@/pages/Workspace/Settings'))
const WorkspaceNotificationSettings = loadable(() => import('@/pages/Workspace/NotificationSettings'))
const WorkspaceMembers = loadable(() => import('@/pages/Workspace/Members'))
const WorkspacePricing = loadable(() => import('@/pages/Workspace/Pricing'))

// Project
const Projects = loadable(() => import('@/pages/Project/Projects'))

// dashboard
// const DashboardEdit = loadable(() => import('@/pages/DataCamp/Dashboard/Edit'))
// const DashboardMain = loadable(() => import('@/pages/DataCamp/Dashboard/Main'))
// const WidgetConfig = loadable(() => import('@/pages/DataCamp/Dashboard/WidgetConfig/WidgetConfig'))

// DataStorage
const Storage = loadable(() => import('@/pages/Storage/Storage'))

// Database
// const DatabaseList = loadable(() => import('@/pages/DataCamp/Database/List'))
// const CollectionList = loadable(() => import('@/pages/DataCamp/Database/Collections/List'))
// const CollectionCreate = loadable(() => import('@/pages/DataCamp/Database/Collections/Create'))
// const CollectionDetail = loadable(() => import('@/pages/DataCamp/Database/Collections/Detail'))

const RenewedPipeline = loadable(() => import('@/pages/RenewedPipeline/RenewedPipeline'))
const OpsPipeline = loadable(() => import('@/pages/RenewedPipeline/OpsPipeline'))

// const LabelingDetail = loadable(() => import('@/pages/Labeling/Detail/Detail'))
// const LabelingCls = loadable(() => import('@/pages/Labeling/Detail/Classification/Detail'))
const LabelingDetail = loadable(() => import('@/pages/Labeling/Detail/DetailMerged'))

const ModelResult = loadable(() => import('@/pages/Result/Model/ModelResult'))
const ValidationResult = loadable(() => import('@/pages/Result/Validation/ValidationResult'))
const InferenceResult = loadable(() => import('@/pages/Result/Inference/InferenceResult'))
const IeModelResult = loadable(() => import('@/pages/Result/Model/IeModelResult'))
const IeValidationResult = loadable(() => import('@/pages/Result/Validation/IeValidationResult'))
const IeInferenceResult = loadable(() => import('@/pages/Result/Inference/IeInferenceResult'))
const DataDriftResult = loadable(() => import('@/pages/Result/DataDrift/DataDriftResult'))
const ModelDriftResult = loadable(() => import('@/pages/Result/ModelDrift/ModelDriftResult'))
const ModelPerformanceResult = loadable(() => import('@/pages/Result/ModelPerformance/ModelPerformanceResult'))
const ModelRetrainEvaluationResult = loadable(() => import('@/pages/Result/ModelRetrainEvaluationResult/ModelRetrainEvaluationResult'))
// user settings

const InquiryList = loadable(() => import('@/pages/User/Inquiries/List'))
const InquiryDetail = loadable(() => import('@/pages/User/Inquiries/Detail'))
const UserSettings = loadable(() => import('@/pages/User/UserSettings/UserSettings'))

// new project
const ProjectMain = loadable(() => import('@/pages/Project/Main'))
const ProjectModelDownloadHistory = loadable(() => import('@/pages/Project/ModelDownloadHistory'))
const ProjectModelRetrainHistory = loadable(() => import('@/pages/Project/ModelRetrainHistory'))

// Manual
const Manual = loadable(() => import('@/pages/Manual/Manual'))

const routes = (isSignIn?: boolean): RouteObject[] => [
  {
    path: SiteUrls.Landing,
    // element: isSignIn ? <Navigate to={SiteUrls.Main._} replace /> : <NotSignedInLayout />,
    element: <NotSignedInLayout />,
    children: [
      {
        path: '',
        element: <Navigate to={SiteUrls.Login._} replace />,
      },
      {
        path: SiteUrls.Login._,
        element: <LoginLayout />,
        children: [
          {
            path: SiteUrls.Login._,
            element: <LoginMain />,
          },
          {
            path: SiteUrls.Login.SignUp,
            element: <SignUp />,
          },
          {
            path: SiteUrls.Login.RecoverPassword,
            element: <RecoverPassword />,
          },
          {
            path: SiteUrls.Login.ExpiredInvitation,
            element: <ExpiredUrl />,
          },
        ],
      },
    ],
  },
  {
    path: SiteUrls.Main._,
    // element: isSignIn ? <MainLayout /> : <Navigate to={SiteUrls.Login._} replace />,
    element: <MainLayout />,
    children: [
      { path: '', element: <Navigate to="workspace/projects" replace /> },
      {
        path: SiteUrls.Main.Workspace.Join,
        element: <JoinWorkspace />,
      },
      {
        path: SiteUrls.Main.Workspace.Create,
        element: <CreateWorkspace />,
      },
      {
        path: SiteUrls.Main.Workspace.NoWorkspace,
        element: <NoWorkspace />,
      },
      {
        path: SiteUrls.Main.Workspace.Main,
        element: <WorkspaceMain />,
        children: [
          { path: '', element: <Navigate to="projects" replace /> },
          {
            path: SiteUrls.Main.Workspace.Settings,
            element: <WorkspaceSettings />,
          },
          {
            path: SiteUrls.Main.Workspace.NotificationSettings,
            element: <WorkspaceNotificationSettings />,
          },
          {
            path: SiteUrls.Main.Workspace.Projects,
            element: <Projects />,
          },
          {
            path: SiteUrls.Main.Workspace.Members,
            element: <WorkspaceMembers />,
          },
          {
            path: SiteUrls.Main.Workspace.Pricing,
            element: <WorkspacePricing />,
          },
          {
            path: SiteUrls.Main.Workspace.Storage._,
            element: <Storage />,
          },
          {
            path: SiteUrls.Main.Workspace.Storage.folder(':id'),
            element: <Storage />,
          },
        ],
      },
      {
        path: SiteUrls.Main.Workspace.Invited(':invitationCode'),
        element: <WorkspaceMain />,
      },
      {
        path: SiteUrls.Main.Project.Main(':projectId'),
        element: <ProjectMain />,
        children: [
          { path: '', element: <Navigate to="pipeline" replace /> },
          {
            path: SiteUrls.Main.Project.ModelDownloadHistory(':projectId'),
            element: <ProjectModelDownloadHistory />,
          },
          {
            path: SiteUrls.Main.Project.ModelRetrainHistory(':projectId'),
            element: <ProjectModelRetrainHistory />,
          },
          {
            path: SiteUrls.Main.Project.ModelManagement(':projectId'),
            element: <p>모델 관리</p>,
          },
          {
            path: SiteUrls.Main.Project.Dashboard(':projectId'),
            element: <p>대시보드</p>,
          },
          {
            path: SiteUrls.Main.Project.Pipeline(':projectId'),
            element: <RenewedPipeline />,
          },
          {
            path: SiteUrls.Main.Project.OpsPipeline(':projectId'),
            element: <OpsPipeline />,
          },
          // 노드 상세 페이지들
          {
            path: SiteUrls.Main.Project.Labeling.Detail(':projectId', ':nodeId'),
            element: <LabelingDetail />,
          },
          {
            path: SiteUrls.Main.Project.Result.Model(':projectId', ':nodeId'),
            element: <ModelResult />,
          },
          {
            path: SiteUrls.Main.Project.Result.ModelIe(':projectId', ':nodeId'),
            element: <IeModelResult />,
          },
          {
            path: SiteUrls.Main.Project.Result.Validation(':projectId', ':nodeId'),
            element: <ValidationResult />,
          },
          {
            path: SiteUrls.Main.Project.Result.ValidationIe(':projectId', ':nodeId'),
            element: <IeValidationResult />,
          },
          {
            path: SiteUrls.Main.Project.Result.Inference(':projectId', ':nodeId'),
            element: <InferenceResult />,
          },
          {
            path: SiteUrls.Main.Project.Result.InferenceIe(':projectId', ':nodeId'),
            element: <IeInferenceResult />,
          },
          {
            path: SiteUrls.Main.Project.Result.DataDrift(':projectId', ':nodeId'),
            element: <DataDriftResult />,
          },
          {
            path: SiteUrls.Main.Project.Result.ModelDrift(':projectId', ':nodeId'),
            element: <ModelDriftResult />,
          },
          {
            path: SiteUrls.Main.Project.Result.ModelPerformance(':projectId', ':nodeId'),
            element: <ModelPerformanceResult />,
          },
          {
            path: SiteUrls.Main.Project.Result.ModelRetrainEvaluation(':projectId', ':nodeId'),
            element: <ModelRetrainEvaluationResult />,
          },
        ],
      },
      {
        path: '',
        element: <Projects />,
      },
      // {
      //   path: SiteUrls.Main.Dashboard._,
      //   element: <DashboardMain />,
      // },
      // {
      //   path: SiteUrls.Main.Dashboard.Edit,
      //   element: <DashboardEdit />,
      // },
      // {
      //   path: SiteUrls.Main.Dashboard.WidgetConfig,
      //   element: <WidgetConfig />,
      // },
      // {
      //   path: SiteUrls.Main.Labeling.Detail(':id'),
      //   element: <LabelingDetail />,
      // },
      // {
      //   path: SiteUrls.Main.Labeling.Classification(':id'),
      //   element: <LabelingCls />,
      // },
      // {
      //   path: SiteUrls.Main.Result.Model(':projectId', ':nodeId'),
      //   element: <ModelResult />,
      // },
      // {
      //   path: SiteUrls.Main.Result.ModelIe(':projectId', ':nodeId'),
      //   element: <IeModelResult />,
      // },
      // {
      //   path: SiteUrls.Main.Result.Validation(':projectId', ':nodeId'),
      //   element: <ValidationResult />,
      // },
      // {
      //   path: SiteUrls.Main.Result.ValidationIe(':projectId', ':nodeId'),
      //   element: <IeValidationResult />,
      // },
      // {
      //   path: SiteUrls.Main.Result.Inference(':projectId', ':nodeId'),
      //   element: <InferenceResult />,
      // },
      // {
      //   path: SiteUrls.Main.Result.InferenceIe(':projectId', ':nodeId'),
      //   element: <IeInferenceResult />,
      // },
      {
        path: SiteUrls.Main.User.Settings,
        element: <UserSettings />,
      },
      {
        path: SiteUrls.Main.User.Inquiries,
        element: <InquiryList />,
      },
      {
        path: SiteUrls.Main.User.InquiryDetail(':id'),
        element: <InquiryDetail />,
      },
      {
        path: SiteUrls.Main.Manual.List,
        element: <Manual />,
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to={SiteUrls.Main.Workspace.NoWorkspace} replace />,
  },
]

export default routes
